import { useEffect, useRef, useState, type ReactNode } from 'react'

/**
 * Fades an element up into place the first time it enters the viewport.
 *
 * `delay` staggers siblings so a row of cards arrives one after another rather
 * than all at once. Anything already on screen at load animates immediately.
 *
 * Reduced-motion users get the finished state with no transition: the content
 * is identical either way, only the arrival differs.
 */
export default function Reveal({
  children,
  delay = 0,
  className = '',
}: {
  children: ReactNode
  delay?: number
  className?: string
}) {
  const ref = useRef<HTMLDivElement>(null)
  const reduced =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  const [shown, setShown] = useState(reduced)

  useEffect(() => {
    if (reduced) return
    const el = ref.current
    if (!el) return

    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setShown(true)
            observer.disconnect()  // reveal once, never re-hide on scroll back
          }
        }
      },
      // Fire slightly before the element is fully in view so the motion reads
      // as the section arriving, not as something catching up late.
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [reduced])

  return (
    <div
      ref={ref}
      className={`${className} transition-[opacity,transform] duration-700 ease-out ${
        shown ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
      }`}
      style={{ transitionDelay: shown ? `${delay}ms` : '0ms' }}
    >
      {children}
    </div>
  )
}
